<template>
  <div class="app-activation-info container-box">
    <div class="container-content">
      <div class="content-left">
        <div>
          <h2 class="text-3xl headline">
            Willkommen bei Lidl <span lang="en">Connect</span>
          </h2>
          <p class="text-base">
            Zur Freischaltung deiner Lidl <span lang="en">Connect</span> <span class="uppercase"><abbr title="Subscriber Identity Module">Sim</abbr></span
            >-Karte benötigst du deine Lidl <span lang="en">Connect</span> Rufnummer und deine
            Freischaltnummer. Beides findest du auf der Rückseite deines Starterpaketes.<br /><br />Wir wünschen dir viel Freude
            mit Lidl <span lang="en">Connect</span>.
          </p>
        </div>

        <form
          @submit.prevent="next()"
          :id="'form' + _uid"
          :novalidate="false"
          data-form-activation-info
          aria-label="Formular zur SIM-Karten Aktivierung"
        >
          <div class="inputs-container" role="group" aria-label="Eingabefelder für die Aktivierung">
            <div class="input-field">
              <label class="text-base-medium" for="activation-msisdn-input">
                Lidl <span lang="en">Connect</span> Rufnummer
                <span class="required" aria-hidden="true">*</span>
              </label>
              <ValidationIndicator :dirty="$v.msisdn.$dirty" :valid="!$v.msisdn.$invalid">
                <input
                  id="activation-msisdn-input"
                  class="input"
                  type="tel"
                  name="msisdn"
                  placeholder="Rufnummer hier eingeben"
                  data-msisdn
                  required
                  aria-required="true"
                  aria-invalid="$v.msisdn.$error"
                  aria-describedby="msisdn-hint"
                  aria-errormessage="msisdn-error"
                  @input="msisdnFormatted"
                  @blur="$v.msisdn.$touch"
                  @click="$v.msisdn.$reset"
                  v-model.trim="msisdn"
                  :class="{
                    'input--invalid': $v.msisdn.$invalid && $v.msisdn.$dirty,
                    'input--valid': !$v.msisdn.$invalid && $v.msisdn.$dirty
                  }"
                />
              </ValidationIndicator>
              <small id="msisdn-hint" class="sr-only">Pflichtfeld</small>
              <div
                id="msisdn-error"
                class="error-message"
                v-if="$v.msisdn.$dirty && $v.msisdn.$error && !isValid('msisdn')"
                role="alert"
              >
                <span v-if="$v.msisdn.required === false"> Bitte gib deine LIDL Connect Rufnummer ein.<br /> </span>
              </div>
            </div>

            <div class="input-field">
              <label class="text-base-medium" for="activation-code-input"
                >Freischaltnummer
                <span class="required" aria-hidden="true">*</span>
              </label>
              <ValidationIndicator :dirty="$v.activationCode.$dirty" :valid="!$v.activationCode.$invalid">
                <input
                  id="activation-code-input"
                  class="input"
                  type="text"
                  placeholder="••••••••••"
                  data-activation-code
                  required
                  @blur="$v.activationCode.$touch"
                  @click="$v.activationCode.$reset"
                  v-model.trim="activationCode"
                  :class="{
                    'input--invalid': $v.activationCode.$invalid && $v.activationCode.$dirty,
                    'input--valid': !$v.activationCode.$invalid && $v.activationCode.$dirty
                  }"
                  aria-describedby="code-hint"
                  aria-errormessage="code-error"
                />
              </ValidationIndicator>
              <small id="code-hint" class="sr-only">Pflichtfeld</small>
              <div id="code-error" class="error-message" v-if="$v.activationCode.$dirty && !isValid('activationCode')">
                <span v-if="$v.activationCode.required === false"> Bitte gib deine Freischaltnummer ein.<br /> </span>
                <span v-if="!$v.activationCode.numeric || !$v.activationCode.minLength || !$v.activationCode.nmaxLengthumeric">
                  Die <strong>Freischaltnummer</strong> muss fünfstellig sein und nur aus Zahlen bestehen.
                </span>
              </div>
            </div>
          </div>
          <button class="button button--primary" :id="'submit-' + _uid" type="submit" aria-busy="loading" :disabled="loading">
            Weiter
          </button>
        </form>
      </div>
      <div class="content-right">
        <img class="info-img" src="files\lidl\images\registrierung\LIDL-Connect-SIM-Karte.png" alt="LIDL Connect SIM-Karte" />
        <div class="info-box">
          <div class="title flex-align-center">
            <span class="icon" aria-hidden="true"></span>
            <span class="text-base-semibold">Freischaltnummer</span>
          </div>
          <span class="text-sm"
            >Die Freischaltnummer entspricht den letzten fünf Stellen deiner
            <span class="uppercase"><abbr title="Subscriber Identity Module">Sim</abbr></span
            >-Kartennummer. Beides findest du auf der Rückseite deines Starterpaketes.</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { numeric, required, maxLength, minLength } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import processSteps from '@/mixins/processSteps'
import { stepName as nextProcessStep } from '../PersonalData/EnterPersonalData.vue'
import isValid from '@/mixins/isValid'
import preFilledInput from '@/mixins/preFilledInput'
import pplusAcceptor from '@/mixins/pplusAcceptor'
import orderByAgentChecker from '@/mixins/orderByAgentChecker'
import msisdnFormatter from '@/mixins/msisdnFormatter'
import ValidationIndicator from '@/components-v2/generic/ValidationIndicator.vue'

const OTHER_BRAND = 'OTHER_BRAND'
export const stepName = 'activationInfo'

export default {
  name: 'ActivationInfo',
  data() {
    return {
      msisdn_: null,
      activationCode_: null,
      displayErrorMessage: false,
      loading: false
    }
  },
  components: {
    ValidationIndicator
  },
  props: {
    activationCodeMin: {
      type: Number,
      default: 4
    },
    activationCodeMax: {
      type: Number,
      default: 5
    },
    otherBrandUrl: {
      type: String,
      default: '#'
    }
  },
  methods: {
    next() {
      if (this.$v.$invalid) {
        return
      }
      this.loading = true
      const msisdnStrip = this.msisdn.replace(/ /g, '')
      this.activationInfo({
        msisdn: msisdnStrip,
        activationCode: this.activationCode,
        nextStep: this.nextStep
      }).then(
        type => {
          if (type === OTHER_BRAND) {
            window.location.href = this.otherBrandUrl + msisdnStrip
          }
          this.loading = false
          this.msisdn = null
          this.activationCode = null
        },
        () => {
          this.loading = false
        }
      )
    },
    ...mapActions('activation/info', ['activationInfo'])
  },
  computed: {
    ...mapGetters('activation/info', {
      getMsisdn: 'msisdn',
      getActivationCode: 'activationCode'
    })
  },
  mixins: [
    validationMixin,
    isValid,
    pplusAcceptor(),
    orderByAgentChecker(),
    msisdnFormatter,
    processSteps(stepName, stepName, nextProcessStep), // prev = current, because first step
    preFilledInput('activationCode', 'getActivationCode'),
    preFilledInput('msisdn', 'getMsisdn')
  ],
  validations() {
    return {
      msisdn: {
        required
      },
      activationCode: {
        required,
        numeric,
        minLength: minLength(this.activationCodeMin),
        maxLength: maxLength(this.activationCodeMax)
      }
    }
  }
}
</script>
